// components/tabs/LeaveRulesTab.jsx
import React from 'react';
import { Save, ShieldCheck, Clock, Loader2 } from 'lucide-react';

export default function LeaveRulesTab({
    leaveRules,
    handleRuleDaysChange,
    handleRuleToggleChange,
    handleSaveRules,
    isSaving
}) {
    // 🌟 Safety check: If rules haven't been mapped yet, show a clean loading spinner
    if (!leaveRules || leaveRules.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-slate-200/60 p-12 flex items-center justify-center gap-2 text-xs font-semibold text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin text-[#002855]" />
                <span>Syncing live parameters...</span>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border font-dynamic-title border-slate-200/60 shadow-xs p-6 space-y-6 transition-all duration-300">
            {/* Context Heading */}
            <div>
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Employee Leave Parameters</h2>
                <p className="text-xs text-slate-400 mt-0.5">Configure global annual allocations and payment rules for standard organizational leave categories.</p>
            </div>
            <hr className="border-slate-100" />

            {/* Matrix Rule Mapping Loop */}
            <div className="space-y-3">
                <div className="grid grid-cols-12 gap-4 px-4 py-2 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <div className="col-span-5">Leave Category Classification</div>
                    <div className="col-span-4 text-center">Maximum Permitted Allowance (Days)</div>
                    <div className="col-span-3 text-center">Compensation Status</div>
                </div>

                <div className="divide-y divide-slate-100 border border-slate-200/60 rounded-xl overflow-hidden bg-white">
                    {leaveRules.map((rule, index) => (
                        <div 
                            key={rule.leave_type} 
                            className="grid grid-cols-12 gap-4 items-center px-4 py-3.5 hover:bg-slate-50/50 transition-colors"
                        >
                            {/* Category Name label column */}
                            <div className="col-span-5 flex items-center gap-2.5">
                                <div className="w-2 h-2 rounded-full bg-blue-500" />
                                <span className="text-xs font-bold text-slate-700">{rule.leave_type}</span>
                            </div>

                            {/* Quantitative Days Input Column */}
                            <div className="col-span-4 flex justify-center">
                                <div className="relative max-w-[120px] w-full">
                                    <input 
                                        type="number"
                                        min="0"
                                        max="365"
                                        // 🌟 Fallback ensures value is never undefined or completely invisible
                                        value={rule.max_allowed_days ?? 0} 
                                        onChange={(e) => handleRuleDaysChange(index, e.target.value)}
                                        className="w-full text-center text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-lg py-1.5 pr-8 pl-3 focus:bg-white focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-hidden transition-all"
                                    />
                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 uppercase">Days</span>
                                </div>
                            </div>

                            {/* Boolean Is Paid toggle option */}
                            <div className="col-span-3 flex items-center justify-center">
                                <button
                                    type="button"
                                    onClick={() => handleRuleToggleChange(index, !rule.is_paid)}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border transition-all cursor-pointer ${
                                        rule.is_paid 
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200/60' 
                                            : 'bg-amber-50 text-amber-700 border-amber-200/60'
                                    }`}
                                >
                                    {rule.is_paid ? (
                                        <>
                                            <ShieldCheck className="w-3 h-3" />
                                            <span>Paid Leave</span>
                                        </>
                                    ) : (
                                        <>
                                            <Clock className="w-3 h-3" />
                                            <span>Unpaid (LWOP)</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <hr className="border-slate-100" />

            {/* Submit Action Block */}
            <div className="flex items-center justify-end pt-2">
                <button
                    type="button"
                    onClick={handleSaveRules}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 disabled:opacity-60 rounded-xl shadow-xs transition-all active:scale-98 cursor-pointer gap-2"
                >
                    <Save className="w-3.5 h-3.5" />
                    {isSaving ? "Saving Rules..." : "Save Leave Rules"}
                </button>
            </div>
        </div>
    );
}